import { Fragment, type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { type ValueOf } from '@direct-frontend/stdlib';
import { trackGoal } from '@/entities/trackGoal';
import { type OrderDto } from '@/shared/api/order';
import { buildLocation } from '@/shared/lib/buildLocation';
import { getRequestContext } from '@/shared/lib/requestContext';
import { CourierNewAddressDialog } from './components/CourierNewAddressDialog';
import { CourierSavedAddressesDialog } from './components/CourierSavedAddressesDialog';
import type { CourierAddressPickerValue } from './lib/types/CourierAddressPickerValue';
import { COURIER_ADDRESS_FORM_ID, courierAddressForm } from './model/courierAddressForm';

const DIALOGS = {
    SAVED_ADDRESSES: 'saved-address',
    NEW_ADDRESS: 'new-address',
} as const;

interface AddressPickerPayload {
    onOpenModal: VoidFunction;
}

const buildInitialValue = (value: CourierAddressPickerValue) => {
    return {
        fullAddress: buildLocation(value),
        appartment: value.appartment,
        floor: value.floor,
        entrance: value.entrance,
        intercom: value.intercom,
        comment: value.comment,
    };
};

export interface AddressPickerProps {
    order: OrderDto;
    reinitializeOrderForm: (initialOrder: OrderDto, updatedOrder: OrderDto) => void;
    storeSlug: string;
    locality: string;
    value?: CourierAddressPickerValue;
    onChange?: (value: CourierAddressPickerValue) => void;
    children: (payload: AddressPickerPayload) => ReactElement;
}

export const CourierAddressPicker = (props: AddressPickerProps) => {
    const { account } = getRequestContext();
    const [activeDialog, setActiveDialog] = useState<ValueOf<typeof DIALOGS> | null>(null);
    const { order, storeSlug, locality, value, children, onChange, reinitializeOrderForm } = props;

    const predefinedAddresses: CourierAddressPickerValue[] = useMemo(
        () =>
            account.addresses
                .filter((address) => address.locality === locality)
                .map((address) => {
                    return {
                        locality: address.locality,
                        address: address.address,
                        floor: address.floor,
                        appartment: address.appartment,
                        entrance: address.entrance,
                        intercom: address.intercom,
                        comment: address.comment,
                    };
                }),
        [account, locality]
    );

    const onOpenModal = useCallback(() => {
        trackGoal('address_popup_open', {
            has_saved_adresses: predefinedAddresses.length > 0,
            saved_addresses_count: predefinedAddresses.length,
        });

        if (predefinedAddresses.length > 0) {
            setActiveDialog(DIALOGS.SAVED_ADDRESSES);

            return;
        }

        setActiveDialog(DIALOGS.NEW_ADDRESS);
    }, [predefinedAddresses, setActiveDialog]);

    const onCloseModal = useCallback(() => setActiveDialog(null), [setActiveDialog]);
    const onAddAddress = useCallback(() => {
        setActiveDialog(DIALOGS.NEW_ADDRESS);
    }, [setActiveDialog]);

    const initialValue = useMemo(() => {
        if (value) {
            return buildInitialValue({ ...value, locality: value.locality || locality });
        }

        return undefined;
    }, [locality, value]);

    useEffect(() => {
        const courierFormInstance = courierAddressForm.getInstance(COURIER_ADDRESS_FORM_ID);

        if (!courierFormInstance?.isInitialized()) {
            return;
        }

        if (
            initialValue?.fullAddress !== courierFormInstance.getValue().fullAddress &&
            !courierFormInstance.getState().isSubmitting
        ) {
            courierFormInstance.reinitialize(initialValue);
        }
    }, [initialValue]);

    return (
        <Fragment>
            <CourierNewAddressDialog
                isOpen={activeDialog === DIALOGS.NEW_ADDRESS}
                context={{ initialOrder: order, reinitializeOrderForm, storeSlug }}
                initialValue={initialValue}
                onChange={onChange}
                onClose={onCloseModal}
            />
            <CourierSavedAddressesDialog
                isOpen={activeDialog === DIALOGS.SAVED_ADDRESSES}
                items={predefinedAddresses}
                onClose={onCloseModal}
                onAddAddress={onAddAddress}
                onChange={onChange}
            />
            {children({ onOpenModal })}
        </Fragment>
    );
};
